import HeaderWordmark from '../shared/HeaderWordmark';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import AppNav from '../shared/AppNav';
import { useUiV2 } from '../../config/uiVersion';
import { TOAST_MS } from '../ui/Toast';
import UserSearch from '../shared/UserSearch';
import TaskUndoToast from '../shared/TaskUndoToast';
import DropCancelConfirm from '../shared/DropCancelConfirm';
import TaskCard from './TaskCard';
import TaskBoardV2 from '../TasksV2/TaskBoardV2';
import useTaskBoard, { GROUP_BY_STORAGE_KEY, SHOW_CLOSED_STORAGE_KEY } from '../../hooks/useTaskBoard';
import {
    CLOSED_WINDOW_DAYS,
    columnEmptyText,
    GROUP_OPTIONS,
    kbdHintText,
    QUICK_VIEWS,
    TYPE_DOT_TONE,
    TYPE_FILTERS,
} from '../../utils/taskBoard';
import '../InventoryDashboard/InventoryDashboard.css';
import './TaskBoard.css';

// Kept for existing imports; they live in hooks/useTaskBoard.js now.
export { GROUP_BY_STORAGE_KEY, SHOW_CLOSED_STORAGE_KEY };

// How long the "Task completed · Undo" toast stays up. One value for the board
// and the v2 Toast primitive (components/ui/Toast.js).
export { TOAST_MS };

const KBD_HINT_ID = 'task-board-kbd-hint';

/**
 * /tasks: the redesigned board with the "new design" flag on, today's board
 * otherwise. Both run on hooks/useTaskBoard (and each card on
 * hooks/useTaskCard), so their behaviour cannot drift.
 */
export default function TaskBoard(props) {
    const uiV2 = useUiV2();
    return uiV2 ? <TaskBoardV2 orgName={props.orgName} /> : <TaskBoardV1 {...props} />;
}

TaskBoard.propTypes = {
    onLogout: PropTypes.func,
    user: PropTypes.object,
    orgName: PropTypes.string,
};

function TaskBoardV1({ onLogout, user, orgName }) {
    const board = useTaskBoard({ orgName });
    const {
        canEdit, readOnly, dealership, subtitle, activities, users, loading,
        unitsLoading, unitFor, unitStatusFor, canOpenStock, nameFor,
        search, setSearch, typeFilter, setTypeFilter, assignee: filterAssignee, setAssignee: setFilterAssignee,
        quickView, toggleQuickView, groupBy, chooseGroupBy, showClosed, chooseShowClosed,
        clearFilters, filtersActive, filtered, hiddenClosed, columns, quickCounts,
        boardRef, dragEnabled, dragItem, setDragItem, dropTarget, setDropTarget, handleDrop, endDrag,
        handleStatusChange, handleQuickStatus, handleEdit, handleDelete, handleNavigate,
        toast, undo, dismissToast,
    } = board;
    const [showUserMenu, setShowUserMenu] = useState(false);
    const navigate = useNavigate();

    const displayName = user?.name || user?.username || 'User';
    const userInitial = displayName.charAt(0).toUpperCase();

    // Today's handshake: the inventory opens the unit's pane on Activity.
    const handleOpenUnit = (stockNumber) => {
        if (!canOpenStock(stockNumber)) return;
        sessionStorage.setItem('pendingOpenUnit', JSON.stringify({ stockNumber, tab: 'activity' }));
        navigate('/');
    };

    return (
        <div className="task-board-page">
            <header className="dashboard-header">
                <div className="header-left">
                    <HeaderWordmark />
                    <div>
                        <p className="subtitle">Task Board</p>
                    </div>
                    {/* Same orientation cue as the dashboard header — whose tasks
                        these are. */}
                    {dealership && (
                        <span className="header-org" title="Dealership currently in view">{dealership}</span>
                    )}
                    {readOnly && (
                        <span className="header-readonly" title="You can look at this dealership's data but not change it">
                            <span className="header-readonly-dot" aria-hidden="true" />
                            Read-only
                        </span>
                    )}
                </div>
                <div className="header-right">
                    <AppNav current="tasks" />
                    <div className="user-menu-wrapper">
                        <button className="btn-header btn-user" onClick={() => setShowUserMenu(v => !v)}>
                            <span className="user-avatar">{userInitial}</span>
                            <span className="user-name">{displayName}</span>
                            <svg className={`chevron ${showUserMenu ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                        {showUserMenu && (
                            <div className="user-dropdown">
                                <div className="dropdown-divider" />
                                <button className="dropdown-item dropdown-logout" onClick={onLogout}>
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                                    </svg>
                                    Sign Out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div className="task-board-toolbar">
                <div className="task-board-toolbar-left">
                    <h2 className="task-board-title">Task Board</h2>
                    {subtitle && <span className="task-board-subtitle">{subtitle}</span>}
                </div>
                <div className="task-board-seg" role="group" aria-label="Group cards by">
                    {GROUP_OPTIONS.map(g => (
                        <button key={g.id} type="button" aria-pressed={groupBy === g.id} onClick={() => chooseGroupBy(g.id)}>
                            {g.label}
                        </button>
                    ))}
                </div>
            </div>

            <p id={KBD_HINT_ID} className="sr-only">
                {kbdHintText(canEdit)}
            </p>

            {loading ? (
                <div className="task-board-loading">Loading tasks...</div>
            ) : (
                <>
                    {/* Quick views: each tile counts all open work and toggles a filter. */}
                    <div className="task-board-summary" role="group" aria-label="Quick views">
                        {QUICK_VIEWS.map(q => (
                            <button
                                key={q.id}
                                type="button"
                                className={`task-stat${q.tone ? ` task-stat--${q.tone}` : ''}`}
                                aria-pressed={quickView === q.id}
                                onClick={() => toggleQuickView(q.id)}
                            >
                                <span className="task-stat-n">{quickCounts[q.id]}</span>{" "}
                                <span className="task-stat-k">{q.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="task-board-filters">
                        <label className="task-search">
                            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                            <span className="sr-only">Search tasks</span>
                            <input
                                type="search"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Stock #, model, task or branch"
                                autoComplete="off"
                            />
                        </label>
                        <div className="task-chips" role="group" aria-label="Task type">
                            {TYPE_FILTERS.map(f => (
                                <button
                                    key={f.id}
                                    type="button"
                                    className="task-chip"
                                    aria-pressed={typeFilter === f.id}
                                    onClick={() => setTypeFilter(f.id)}
                                >
                                    {f.id !== 'all' && <span className={`task-chip-dot task-chip-dot--${TYPE_DOT_TONE[f.id]}`} aria-hidden="true" />}
                                    {f.label}
                                </button>
                            ))}
                        </div>
                        <UserSearch
                            users={users}
                            value={filterAssignee}
                            onChange={setFilterAssignee}
                            activities={activities}
                            placeholder="All assignees"
                            allLabel="All assignees"
                        />
                        {unitsLoading && (
                            <span className="task-board-hint" role="status">Loading unit details…</span>
                        )}
                        <span className="task-board-filters-spacer" />
                        <label className="task-board-toggle">
                            <input type="checkbox" checked={showClosed} onChange={(e) => chooseShowClosed(e.target.checked)} />
                            {showClosed ? `Show closed (last ${CLOSED_WINDOW_DAYS} days)` : `Show closed (${hiddenClosed})`}
                        </label>
                    </div>

                    {filtersActive && filtered.length === 0 ? (
                        <div className="task-board-nomatch" role="status">
                            <p>No tasks match</p>
                            <button type="button" className="task-btn" onClick={clearFilters}>Clear filters</button>
                        </div>
                    ) : groupBy === 'branch' && unitsLoading ? (
                        <div className="task-board-nomatch" role="status">
                            <p>Loading branches…</p>
                        </div>
                    ) : (
                    <div className="task-board-scroll">
                        <div className="task-board-columns" ref={boardRef} tabIndex={-1}>
                            {columns.map(col => {
                                const isDropTarget = col.droppable && Boolean(dragItem) && dropTarget === col.key;
                                const headingId = `task-col-${col.key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
                                return (
                                    <section
                                        key={col.key}
                                        className={`task-board-column${isDropTarget ? ' task-board-column--drop' : ''}`}
                                        aria-labelledby={headingId}
                                        data-col-key={col.key}
                                        tabIndex={-1}
                                        onDragOver={(e) => {
                                            if (!dragEnabled || !col.droppable || !dragItem) return;
                                            e.preventDefault();
                                            if (dropTarget !== col.key) setDropTarget(col.key);
                                        }}
                                        onDragLeave={(e) => {
                                            if (!e.currentTarget.contains(e.relatedTarget)) setDropTarget(t => (t === col.key ? null : t));
                                        }}
                                        onDrop={(e) => {
                                            if (!col.droppable) return;
                                            e.preventDefault();
                                            handleDrop(col.key);
                                        }}
                                    >
                                        <div className="task-column-header">
                                            <span className={`task-column-dot task-column-dot--${col.tone}`} aria-hidden="true" />
                                            <h3 className="task-column-label" id={headingId} title={col.label}>{col.label}</h3>
                                            <span className="task-column-count" aria-label={`${col.tasks.length} tasks`}>{col.tasks.length}</span>
                                        </div>
                                        <div className="task-column-cards">
                                            {col.tasks.length === 0 ? (
                                                <div className="task-column-empty">{columnEmptyText({ filtersActive, dragEnabled, droppable: col.droppable })}</div>
                                            ) : (
                                                col.tasks.map(task => (
                                                    <TaskCard
                                                        key={task.id}
                                                        task={task}
                                                        unit={unitFor(task)}
                                                        unitStatus={unitStatusFor(task)}
                                                        assigneeName={task.assigned_to ? nameFor(task.assigned_to) : undefined}
                                                        canEdit={canEdit}
                                                        draggable={dragEnabled}
                                                        onStatusChange={handleStatusChange}
                                                        onQuickStatus={handleQuickStatus}
                                                        onNavigate={handleNavigate}
                                                        hintId={KBD_HINT_ID}
                                                        onOpenUnit={handleOpenUnit}
                                                        onDelete={handleDelete}
                                                        onEdit={handleEdit}
                                                        dragging={dragItem?.id === task.id}
                                                        onDragStart={setDragItem}
                                                        onDragEnd={endDrag}
                                                    />
                                                ))
                                            )}
                                        </div>
                                    </section>
                                );
                            })}
                        </div>
                    </div>
                    )}
                </>
            )}

            {/* Always mounted, so screen readers hear each new message. */}
            <DropCancelConfirm task={board.pendingCancel} onConfirm={board.confirmPendingCancel} onKeep={board.keepPendingCancel} />
            <TaskUndoToast toast={toast} onUndo={undo} onDismiss={dismissToast} />
        </div>
    );
}

TaskBoardV1.propTypes = TaskBoard.propTypes;

